import { uid } from "../kern/format.js";

/* ================================================================
   Katalog — der globale Stammdatensatz.

   Wird im Cloud-Modus einmal je Sitzung geladen und dann für Suche
   und Übernahme genutzt. Er enthält nur gemeinsame Angaben; Bestände,
   Lagerorte und Preise entstehen erst beim Übernehmen im eigenen
   Kellerbuch.

   Übernommene Objekte behalten die Katalog-ID. Dadurch sind sie
   später eindeutig zuzuordnen, ohne dass jetzt schon eine Verknüpfung
   gepflegt werden muss.
   ================================================================ */

const LEER = { regionen: [], winzer: [], weine: [] };

async function katalogLaden(bekannteVersion = null) {
  const pfad = "/api/katalog" + (bekannteVersion != null ? `?version=${bekannteVersion}` : "");
  const r = await fetch(pfad);
  if (r.status === 401) throw Object.assign(new Error("Nicht angemeldet."), { status: 401 });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.fehler || "Der Katalog konnte nicht geladen werden.");
  if (json.unveraendert) return null;          /* Aufrufer behält seinen Stand */
  return { version: json.version, daten: json.daten || LEER };
}

/* Kleine Hilfen für die Suche ---------------------------------- */

const normal = (s) => String(s || "").toLowerCase()
  .replace(/[äàáâ]/g, "a").replace(/[öòóô]/g, "o").replace(/[üùúû]/g, "u")
  .replace(/[éèêë]/g, "e").replace(/ß/g, "ss")
  .replace(/[^a-z0-9]+/g, " ").trim();

/* Sucht Weine im Katalog und liefert sie mit lesbarem Beiwerk. */
function sucheWeine(katalog, text, grenze = 8) {
  const q = normal(text);
  if (q.length < 2) return [];
  const regionById = new Map(katalog.regionen.map((r) => [r.id, r]));
  const winzerById = new Map(katalog.winzer.map((w) => [w.id, w]));
  const treffer = [];
  for (const w of katalog.weine) {
    const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
    const reg = (w.regionId && regionById.get(w.regionId)) || (wz && regionById.get(wz.regionId)) || null;
    const heu = normal(`${w.name} ${wz ? wz.name : ""} ${reg ? reg.name : ""}`);
    if (!heu.includes(q)) continue;
    treffer.push({
      objekt: w, winzer: wz, region: reg,
      /* Namenstreffer am Wortanfang zuerst. */
      rang: normal(w.name).startsWith(q) ? 0 : 1,
      neben: [wz && wz.name, reg && reg.name].filter(Boolean).join(" · "),
    });
    if (treffer.length > 60) break;
  }
  return treffer.sort((a, b) => a.rang - b.rang || a.objekt.name.localeCompare(b.objekt.name, "de")).slice(0, grenze);
}

/* Sucht Winzer bzw. Regionen. */
function sucheEinfach(liste, text, grenze = 8, nebenFn = null) {
  const q = normal(text);
  if (q.length < 2) return [];
  return liste
    .filter((o) => normal(o.name).includes(q))
    .sort((a, b) => {
      const av = normal(a.name).startsWith(q) ? 0 : 1;
      const bv = normal(b.name).startsWith(q) ? 0 : 1;
      return av - bv || a.name.localeCompare(b.name, "de");
    })
    .slice(0, grenze)
    .map((o) => ({ objekt: o, neben: nebenFn ? nebenFn(o) : "" }));
}

/* Alle Weine eines Winzers aus dem Katalog, alphabetisch. Grundlage
   für den Weg „erst den Winzer suchen, dann seine Weine sehen“. */
function weineVonWinzer(katalog, winzerId) {
  if (!katalog || !winzerId) return [];
  return katalog.weine
    .filter((w) => w.winzerId === winzerId)
    .sort((a, b) => String(a.name).localeCompare(String(b.name), "de"));
}

/* ----------------------------------------------------------------
   Übernahme in das eigene Kellerbuch.

   Grundsatz: Nichts überschreiben. Was es lokal schon gibt (gleiche
   ID), bleibt wie es ist. Fehlende Voraussetzungen werden mitgenommen
   — ein Wein braucht seinen Winzer, ein Winzer seine Region.
   ---------------------------------------------------------------- */

function uebernehmeRegion(db, katalog, regionId) {
  if (!regionId) return { db, id: null };
  if (db.regions.some((r) => r.id === regionId)) return { db, id: regionId };
  const kr = katalog.regionen.find((r) => r.id === regionId);
  if (!kr) return { db, id: null };
  return {
    db: { ...db, regions: [...db.regions, { ...kr, herkunft: "katalog" }] },
    id: regionId,
  };
}

function uebernehmeWinzer(db, katalog, winzerId) {
  if (!winzerId) return { db, id: null };
  if (db.winzer.some((w) => w.id === winzerId)) return { db, id: winzerId };
  const kw = katalog.winzer.find((w) => w.id === winzerId);
  if (!kw) return { db, id: null };
  const nachRegion = uebernehmeRegion(db, katalog, kw.regionId);
  return {
    db: { ...nachRegion.db, winzer: [...nachRegion.db.winzer, { ...kw, herkunft: "katalog" }] },
    id: winzerId,
  };
}

/* Übernimmt einen Wein samt Winzer und Region. Jahrgänge kommen als
   leere Hüllen mit Jahr und Trinkfenster — Bestände trägt der Nutzer
   selbst ein, sie gehören nicht in den Katalog. */
function uebernehmeWein(db, katalog, weinId, mitJahrgaengen = true) {
  const kw = katalog.weine.find((w) => w.id === weinId);
  if (!kw) return { db, id: null, schonVorhanden: false };
  if (db.weine.some((w) => w.id === weinId)) return { db, id: weinId, schonVorhanden: true };

  const nachWinzer = uebernehmeWinzer(db, katalog, kw.winzerId);
  const nachRegion = uebernehmeRegion(nachWinzer.db, katalog, kw.regionId);

  const rl = Array.isArray(kw.rebsortenListe) ? kw.rebsortenListe : [];
  const anzeige = rl.length
    ? (rl.every((r) => r.anteil != null)
      ? rl.map((r) => `${r.name} ${Math.round(r.anteil)} %`)
      : rl.map((r) => r.name)).join(" · ")
    : "";

  const wein = {
    id: kw.id, name: kw.name,
    winzerId: nachWinzer.id, regionId: nachRegion.id,
    typ: kw.typ || null, appellation: kw.appellation || "", beschreibung: kw.beschreibung || "",
    rebsortenListe: rl, rebsorten: anzeige,
    herkunft: "katalog",
    jahrgaenge: mitJahrgaengen
      ? (kw.jahrgaenge || []).map((j) => ({
        id: j.id || uid(), jahr: j.jahr,
        trinkVon: j.trinkVon != null ? j.trinkVon : null,
        trinkBis: j.trinkBis != null ? j.trinkBis : null,
        /* Etikett aus dem Katalog getrennt vom eigenen führen: So kann
           ein eigenes Foto es jederzeit überlagern, und das Entfernen
           löscht nie die Datei eines anderen Kontos. */
        katalogFoto: j.foto || null,
        bestaende: {}, lagerort: "", preis: null,
      }))
      : [],
  };
  return {
    db: { ...nachRegion.db, weine: [...nachRegion.db.weine, wein] },
    id: weinId, schonVorhanden: false,
  };
}

export { katalogLaden, sucheEinfach, sucheWeine, uebernehmeRegion, uebernehmeWein, uebernehmeWinzer, weineVonWinzer };
